import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, CalendarClock, Gauge, Download, ArrowUpRight, Clock, Save, Building2 } from "lucide-react";

const STATUS = {
  active: { label: "Active", cls: "tone-powder" },
  trial: { label: "Trial", cls: "bg-slate-100 text-slate-600" },
  pending_verification: { label: "Verifying payment", cls: "bg-amber-100 text-amber-800" },
  past_due: { label: "Past due", cls: "bg-red-100 text-red-700" },
  expired: { label: "Expired", cls: "bg-red-100 text-red-700" },
};
const PAY_STATUS = {
  PAID: { label: "Paid", cls: "tone-powder" },
  PENDING_VERIFICATION: { label: "Verifying", cls: "bg-amber-100 text-amber-800" },
  CREATED: { label: "Awaiting payment", cls: "bg-slate-100 text-slate-600" },
  REJECTED: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  FAILED: { label: "Failed", cls: "bg-red-100 text-red-700" },
};

export default function Billing() {
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState({ gstin: "", billing_address: "", billing_state: "", billing_state_code: "", billing_contact_email: "", po_number: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  async function load() {
    try {
      const m = await api.get("/billing/me");
      setMe(m.data);
      if (m.data.role === "OrgAdmin") {
        const h = await api.get("/billing/history");
        setHistory(h.data.items || []);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  function downloadInvoice(id) {
    const token = localStorage.getItem("meetiq_token");
    fetch(`${BACKEND_URL}/api/billing/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `invoice-${id}.pdf`; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error("Invoice unavailable"));
  }

  async function saveProfile() {
    setSavingProfile(true);
    try { await api.put("/billing/profile", profile); toast.success("Billing details saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingProfile(false); }
  }

  if (!me) return <div className="p-10 text-sm text-muted-foreground">Loading billing…</div>;

  // Member read-only
  if (me.role === "Member") {
    const s = STATUS[me.status] || STATUS.trial;
    return (
      <div data-testid="billing-root" className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl tracking-tight">Billing</h1>
        <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="billing-member-card">
          <div className="text-sm text-muted-foreground">Your organisation</div>
          <div className="font-heading text-2xl mt-1">{me.org_name}</div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-muted-foreground text-xs">Plan</div><div className="font-semibold">{me.plan_name}</div></div>
            <div><div className="text-muted-foreground text-xs">Status</div><span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>{s.label}</span></div>
            <div><div className="text-muted-foreground text-xs">Renews / expires</div><div className="font-semibold">{me.valid_until || "—"}</div></div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">Only your organisation admin can view invoices and change the plan.</p>
        </div>
      </div>
    );
  }

  const s = STATUS[me.status] || STATUS.trial;
  const quota = me.seats ? `${me.seats_used} / ${me.seats}` : `${me.seats_used}`;
  const expiring = me.days_left != null && me.days_left <= 7;

  return (
    <div data-testid="billing-root" className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">{me.org_name} · {me.region === "IN" ? "India" : "International"}</p>
        </div>
        <Link to="/pricing" data-testid="billing-upgrade-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}>
          <ArrowUpRight className="w-4 h-4" /> {me.plan ? "Change plan / Renew" : "Choose a plan"}
        </Link>
      </div>

      {me.provisional_until && me.status === "pending_verification" && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm flex items-center gap-2" data-testid="provisional-banner">
          <Clock className="w-4 h-4" /> Payment received — verifying. You have provisional access until {new Date(me.provisional_until).toLocaleString()}.
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div data-testid="billing-plan-card" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><CreditCard className="w-4 h-4" /> Plan</div>
          <div className="mt-2 font-heading text-2xl">{me.plan_name}</div>
          <div className="text-xs mt-1"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>{s.label}</span>{me.billing_cycle ? <span className="text-muted-foreground"> · {me.billing_cycle}</span> : null}</div>
        </div>
        <div data-testid="billing-validity-card" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><CalendarClock className="w-4 h-4" /> Renews / expires</div>
          <div className="mt-2 font-heading text-2xl" style={{ color: expiring ? "hsl(var(--ink-warning))" : "hsl(var(--navy))" }}>{me.valid_until || "—"}</div>
          <div className="text-xs mt-1 text-muted-foreground">{me.days_left == null ? "—" : me.days_left < 0 ? "Expired" : `${me.days_left} days left`}</div>
        </div>
        <div data-testid="billing-usage-card" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Gauge className="w-4 h-4" /> Seats used</div>
          <div className="mt-2 font-heading text-2xl">{quota}</div>
          <div className="text-xs text-muted-foreground mt-1">{me.seats ? "against purchased seats" : "no active plan"}</div>
        </div>
      </div>

      {/* Billing details */}
      <div className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" /> Billing details</h2>
        <p className="text-xs text-muted-foreground mt-1">Used on your tax invoices. {me.region === "IN" ? "Add your GSTIN for input credit." : ""}</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {me.region === "IN" && <input data-testid="profile-gstin" placeholder="GSTIN (optional)" value={profile.gstin} onChange={(e) => setProfile({ ...profile, gstin: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />}
          <input data-testid="profile-contact" placeholder="Billing contact email" value={profile.billing_contact_email} onChange={(e) => setProfile({ ...profile, billing_contact_email: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          <input data-testid="profile-address" placeholder="Billing address" value={profile.billing_address} onChange={(e) => setProfile({ ...profile, billing_address: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm sm:col-span-2" />
          {me.region === "IN" && <input data-testid="profile-state" placeholder="State (e.g. Tamil Nadu)" value={profile.billing_state} onChange={(e) => setProfile({ ...profile, billing_state: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />}
          {me.region === "IN" && <input data-testid="profile-state-code" placeholder="State code (e.g. 33)" value={profile.billing_state_code} onChange={(e) => setProfile({ ...profile, billing_state_code: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />}
          <input data-testid="profile-po" placeholder="PO number (optional)" value={profile.po_number} onChange={(e) => setProfile({ ...profile, po_number: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <button data-testid="profile-save" onClick={saveProfile} disabled={savingProfile} className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: "hsl(var(--navy))" }}><Save className="w-4 h-4" /> Save details</button>
      </div>

      {/* History */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold">Payment history</h2>
        <div className="mt-3 rounded-2xl border border-border bg-card overflow-x-auto">
          {history.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No orders yet.</div>
          ) : (
            <table className="w-full text-sm min-w-[680px]">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Invoice</th>
              </tr></thead>
              <tbody>
                {history.map((o) => {
                  const ps = PAY_STATUS[o.status] || PAY_STATUS.CREATED;
                  return (
                    <tr key={o.id} data-testid="billing-history-row" className="border-t border-border">
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{(o.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{o.order_ref}</td>
                      <td className="px-4 py-3 font-medium">{o.plan_name} <span className="text-muted-foreground">· {o.billing_cycle}</span></td>
                      <td className="px-4 py-3 font-semibold">{o.amount_display}</td>
                      <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${ps.cls}`}>{ps.label}</span></td>
                      <td className="px-4 py-3 text-right">
                        {o.invoice ? (
                          <button data-testid="invoice-download-btn" onClick={() => downloadInvoice(o.invoice.id)} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "hsl(var(--cyan))" }}><Download className="w-3.5 h-3.5" /> {o.invoice.number}</button>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
